import React, { useState, useEffect, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import DashboardContainer from 'containers/DashboardContainer'
import { flyerDesignLayoutConfig } from 'assets/frontend-json/flyer-position'
import FlyerCard from 'src/containers/event/CreateEvent/SelectFlyerStep/FlyerCard/FlyerCard'
import CustomFlyerPicker from 'src/components/common/CustomFlyerPicker'
import {
  changeEventForm,
  fetchCreateEvent,
  fetchUploadFlyer,
  resetEventForm,
  fetchUploadVideoFlyer,
  fetchZazzleEventInfo
} from 'src/actions/event'
import { fetchFlyerDesigns, setCustomFlyerURL } from 'src/actions/flyer'
import { showLogin } from 'src/actions/login'
import {
  userIdSelector,
  eventFormSelector,
  flyerSelector
} from 'src/selectors'
import PreviewIcon from 'assets/icons/preview-icon.png'
import ImagesIcon from 'src/assets/icons/icon_images.png'
import SelectFlyerStep from 'src/containers/event/CreateEvent/SelectFlyerStep/SelectFlyerStep'
import InputOptions from '../../InputEventDetail/InputOptions/InputOptions'
import LoadingComponent from 'components/common/LoadingComponent/LoadingComponent'
import { deepCopy, generate16to9ImageWithBlur } from 'services/utils'
import moment from 'moment-timezone'
import uuid from 'uuid'
import cn from 'classnames'
import { message, notification } from 'antd'
import { redirectDomain } from 'services/url_helpers'
import { isMobile } from 'services/browser_detection'
import BackIcon from 'src/assets/icons/back_button.png'
import SvgIcon from 'src/components/common/SvgIcon'
import SearchIcon from 'src/assets/icons/ico-search-outlined.svg'
import styles from './zazzle-create-event.scss'
import ImageSearchModal from '../../CreateEvent/ImageSearchModal/ImageSearchModal'
import VideoFlyerCard from '../../CreateEvent/SelectFlyerStep/VideoFlyerCard/VideoFlyerCard'
import SettingIcon from 'assets/icons/setting-icon.png'
import RevertIcon from 'assets/icons/ico-revert.svg'
import Button from 'components/common/Button'
import refreshIcon from 'src/assets/icons/ico-refresh.svg'
import ArrowRightOutlined from 'src/assets/icons/ico-arrow-right-outlined.svg'
import defaultFlyerImg from 'assets/zazzle-default-flyer.png'
import ZazzleIntroductionPageModal from 'src/components/common/ZazzleIntroductionPageModal/ZazzleIntroductionPageModal'
import HobnobModal from 'src/components/common/HobnobModalV2/HobnobModal'
import CloseIcon from 'src/assets/icons/close-icon.png'
import { routerActions } from 'react-router-redux'

const ZazzleCreateEvent = ({ designId, imageUrl, source = 'webpage' }) => {
  const dispatch = useDispatch()
  const [fetchedDesignId, setFetchedDesignId] = useState(null)
  const eventForm = useSelector(eventFormSelector)
  const userId = useSelector(userIdSelector)
  const { flyerList, customFlyerURL } = useSelector(flyerSelector)

  const [selectedFlyerNumber, setSelectedFlyerNumber] = useState(null)
  const [isZoomFlyer, setIsZoomFlyer] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [currentView, setCurrentView] = useState('detail')
  const [isOpenImageSearch, setIsOpenImageSearch] = useState(false)
  const flyerRef = useRef(null)
  const videoFlyerRef = useRef(null)
  const [isImageSearchLoading, setIsImageSearchLoading] = useState(false)
  const initialFlyerRef = useRef(null)
  const initialCustomFlyerURLRef = useRef(null)
  const [zazzleEventInfo, setZazzleEventInfo] = useState(null)
  const [currentImageUrl, setCurrentImageUrl] = useState(imageUrl || '')
  const [isLoadZazzleDesignImageSuccess, setIsLoadZazzleDesignImageSuccess] = useState(true)
  const [isDefaultFlyer, setIsDefaultFlyer] = useState(false)
  const [isLoadingEventInfo, setIsLoadingEventInfo] = useState(true)
  const isZazzleImageUpload = !!designId
  const [showZazzleIntroductionPageModal, setShowZazzleIntroductionPageModal] = useState(false)
  const [canOpenTour, setCanOpenTour] = useState(false)
  const [isRevertZazzleModalVisible, setIsRevertZazzleModalVisible] = useState(false)
  const [blurredImageUrl, setBlurredImageUrl] = useState('')
  const onNavigationList = () => dispatch(routerActions.push({ pathname: '/e/list' }))

  useEffect(() => {
    if (designId && !fetchedDesignId && designId !== fetchedDesignId) {
      setFetchedDesignId(designId)
      setIsLoading(true)

      setIsLoadingEventInfo(true)

      dispatch(
        fetchZazzleEventInfo({
          design_id: designId,
          onSuccessCallback: data => {
            if (window.mixpanel) {
              window.mixpanel.track('Zazzle Event Info Fetched', {
                user_id: userId,
                source: source,
                design_id: designId,
                email: data?.host_email || ''
              })
            }
            setZazzleEventInfo(data)
            if (data.url) {
              redirectDomain(data.url + '/')
              return
            }

            if (source?.toUpperCase() === 'EMAIL') {
              setShowZazzleIntroductionPageModal(true)
            }

            if (data?.name) {
              dispatch(changeEventForm({ name: data.name }))
            }

            if (data?.starts_at) {
              const startsAtInTZ = moment.tz(data.starts_at, 'Pacific/Honolulu').format('YYYY-MM-DD HH:mm')
              dispatch(changeEventForm({ starts_at: startsAtInTZ }))
            }

            if (data?.ends_at) {
              const endsAtInTZ = moment.tz(data.ends_at, 'Pacific/Honolulu').format('YYYY-MM-DD HH:mm')
              dispatch(changeEventForm({ ends_at: endsAtInTZ }))
            }

            if (data?.design_file_url?.Front && data?.design_file_url?.Front !== currentImageUrl) {
              setCurrentImageUrl(data.design_file_url.Front)
              setIsDefaultFlyer(false)
            }

            setIsLoading(false)
          },
          onErrorCallback: () => {
            notification.error({
              description: 'Failed to fetch event info'
            })

            setCurrentImageUrl(defaultFlyerImg)
            setIsLoadZazzleDesignImageSuccess(false)
            setIsDefaultFlyer(true)
            setIsLoadingEventInfo(false)
            setIsLoading(false)
          }
        })
      )
    }
  }, [designId])

  useEffect(() => {
    dispatch(fetchFlyerDesigns())
    if (window.mixpanel) {
      window.mixpanel.track('Create Event Page Load', {
        user_id: userId,
        source: source,
        design_id: designId
      })
    }
    return () => {
      dispatch(resetEventForm())
    }
  }, [])

  useEffect(() => {
    if (eventForm.flyer_design_number) {
      setSelectedFlyerNumber(eventForm.flyer_design_number)
    }
  }, [eventForm.flyer_design_number])

  useEffect(() => {
    if (currentImageUrl) {
      generate16to9ImageWithBlur(currentImageUrl).then(dataUrl => {
        setBlurredImageUrl(dataUrl)
      })
    }
  }, [currentImageUrl])

  const handleChangeFlyer = useCallback(() => {
    setCurrentView('detail')
    setSelectedFlyerNumber(eventForm.flyer_design_number)
  }, [eventForm.flyer_design_number])

  const handleOpenFlyerModal = useCallback(e => {
    e.stopPropagation()
    if (currentView === 'detail') {
      initialFlyerRef.current = {
        flyer_design_number: eventForm.flyer_design_number,
        flyer_design_slug: eventForm.flyer_design_slug,
        checkedFlyer: eventForm.checkedFlyer
      }

      initialCustomFlyerURLRef.current = customFlyerURL
    }
    setCurrentView('flyer')
  }, [currentView, eventForm.flyer_design_number, eventForm.flyer_design_slug, eventForm.checkedFlyer, customFlyerURL])

  const handleBackToDetail = () => {
    dispatch(changeEventForm({
      flyer_design_number: initialFlyerRef.current.flyer_design_number,
      flyer_design_slug: initialFlyerRef.current.flyer_design_slug,
      checkedFlyer: initialFlyerRef.current.checkedFlyer
    }))

    dispatch(setCustomFlyerURL(initialCustomFlyerURLRef.current))
    setSelectedFlyerNumber(initialFlyerRef.current.flyer_design_number)
    setTimeout(() => {
      setCurrentView('detail')
    }, 0)
  }

  const handleSubmit = (params = {}) => {
    if (userId) {
      window.mixpanel.track('Save CHANGES Clicked', {
        'flyer#': selectedFlyerNumber,
        user_id: userId
      })
      handleCreateOrZazzleCreateEvent(params)
      return
    }

    let loginParams = {
      loginModalSubtitle: 'In order for you to create an Event, we need to confirm your number',
      loginSource: 'zazzle'
    }
    if (zazzleEventInfo?.host_email) {
      loginParams = {
        ...loginParams,
        emailAddress: zazzleEventInfo?.host_email
      }
    }

    dispatch(showLogin(loginParams))
  }

  const handleCreateOrZazzleCreateEvent = async ({ apple_place_id }) => {
    if (!selectedFlyerNumber && !isLoadZazzleDesignImageSuccess) {
      message.error('Please select a flyer before creating the event.')
      return
    }

    setIsLoading(true)
    const eventParams = deepCopy(eventForm)
    delete eventParams.checkedFlyer
    delete eventParams.invitations

    if (!selectedFlyerNumber && isLoadZazzleDesignImageSuccess) {
      eventParams.flyer_design_number = 0
      eventParams.flyer_design_slug = 'zazzle-design'
    }

    if (apple_place_id) {
      eventParams.apple_place_id = apple_place_id
    }

    if (eventParams.starts_at) {
      eventParams.starts_at = moment.tz(eventParams.starts_at, eventParams.timezone).format()
      if (eventParams.ends_at) {
        eventParams.ends_at = moment.tz(eventParams.ends_at, eventParams.timezone).format()
      }
    }

    if (eventForm.child_count_enabled) {
      eventParams.ticket_types_attributes = [
        {
          id: uuid.v4(),
          label: 'adult',
          price: 0
        },
        {
          id: uuid.v4(),
          label: 'child',
          price: 0
        }
      ]
    } else {
      eventParams.ticket_types_attributes = [
        {
          id: uuid.v4(),
          label: 'adult',
          price: 0
        }
      ]
    }

    const partnerData = isLoadZazzleDesignImageSuccess ? {
      design_id: designId,
      host_email: zazzleEventInfo?.host_email || '',
      source: source,
      partner_type: 'zazzle',
      design_image_url: currentImageUrl
    } : {}
    dispatch(
      fetchCreateEvent({
        eventId: eventParams.id,
        eventForm: {
          ...eventParams,
          partner_data: { ...partnerData }
        },
        onSuccessCallback: data => {
          const {
            event: { id, url }
          } = data

          if (selectedFlyerNumber) {
            if (eventForm.mediaType === 'video') {
              if (customFlyerURL) {
                handleUploadFlyer(id, url, eventForm.mediaType)
              } else {
                handleUploadVideoFlyer(id, url)
              }
            } else {
              handleUploadFlyer(id, url, eventForm.mediaType)
            }
          } else if (isLoadZazzleDesignImageSuccess) {
            handleUploadFlyer(id, url, 'zazzle')
          }
        },
        onErrorCallback: () => {
          setIsLoading(false)
          message.error('Creation failed.')
        }
      })
    )
  }

  const handleUploadVideoFlyer = (eventId, eventUrl) => {
    dispatch(
      fetchUploadVideoFlyer({
        eventId,
        design_number: eventForm.flyer_design_number,
        imageBgFile: customFlyerURL,
        onSuccessCallback: async data => {
          const requests = []
          if (data?.video_flyer?.image_bg_upload?.presigned_url) {
            const imageBlob = await videoFlyerRef.current?.generateImage()
            if (!imageBlob) {
              alert('Upload Error.')
              setIsLoading(false)
            }
            requests.push(
              fetch(data.video_flyer.image_bg_upload.presigned_url, {
                headers: {
                  'Content-Type': 'image/png'
                },
                method: 'PUT',
                body: imageBlob
              })
            )
          }

          Promise.all(requests).then(responses => {
            if (responses.every(response => response.status === 200)) {
              setTimeout(() => {
                redirectDomain(eventUrl + '/')
                dispatch(resetEventForm())
              }, 5000)
            } else {
              setIsLoading(false)
              alert('Upload Error.')
            }
          })
        },
        onErrorCallback: () => {
          setIsLoading(false)
          message.error('Creation failed.')
        }
      })
    )
  }

  const handleUploadFlyer = (eventId, eventUrl, mediaType) => {
    if (mediaType === 'zazzle') {
      dispatch(
        fetchUploadFlyer({
          eventId,
          design_number: 0,
          customBgDataURL: currentImageUrl,
          onSuccessCallback: async data => {
            const requests = []
            const flyerBgImageResponse = await fetch(blurredImageUrl)
            const flyerBgImageBlob = await flyerBgImageResponse.blob()
            if (data?.image_flyer?.image_upload?.presigned_url) {
              requests.push(
                fetch(data.image_flyer.image_upload.presigned_url, {
                  headers: {
                    'Content-Type': 'image/png'
                  },
                  method: 'PUT',
                  body: flyerBgImageBlob
                })
              )
            }

            if (data?.image_flyer?.image_bg_upload?.presigned_url) {
              requests.push(
                fetch(data.image_flyer.image_bg_upload.presigned_url, {
                  headers: {
                    'Content-Type': 'image/png'
                  },
                  method: 'PUT',
                  body: flyerBgImageBlob
                })
              )
            }

            if (requests.length === 0) {
              setIsLoading(false)
              alert('Upload Error.')
              return
            }

            Promise.all(requests).then(responses => {
              if (responses.every(response => response.status === 200)) {
                setTimeout(() => {
                  redirectDomain(eventUrl + '/')
                  dispatch(resetEventForm())
                }, 5000)
              } else {
                setIsLoading(false)
                alert('Upload Error.')
              }
            })
          }
        })
      )
      return
    }

    dispatch(
      fetchUploadFlyer({
        eventId,
        design_number: selectedFlyerNumber,
        customBgDataURL: customFlyerURL,
        onSuccessCallback: async data => {
          const requests = []
          if (data?.image_flyer?.image_upload?.presigned_url) {
            let flyerImageBlob
            if (mediaType === 'video') {
              flyerImageBlob = await videoFlyerRef.current?.generateImage()
            } else {
              flyerImageBlob = await flyerRef.current?.onCanvasToImage()
            }
            requests.push(
              fetch(data.image_flyer.image_upload.presigned_url, {
                headers: {
                  'Content-Type': 'image/png'
                },
                method: 'PUT',
                body: flyerImageBlob
              })
            )
          }

          if (data?.image_flyer?.image_bg_upload?.presigned_url) {
            const flyerBgImage = await fetch(customFlyerURL)
            const flyerBgImageBlob = await flyerBgImage.blob()
            requests.push(
              fetch(data.image_flyer.image_bg_upload.presigned_url, {
                headers: {
                  'Content-Type': 'image/png'
                },
                method: 'PUT',
                body: flyerBgImageBlob
              })
            )
          }

          if (requests.length === 0) {
            setIsLoading(false)
            alert('Upload Error.')
            return
          }

          Promise.all(requests).then(responses => {
            if (responses.every(response => response.status === 200)) {
              setTimeout(() => {
                redirectDomain(eventUrl + '/')
                dispatch(resetEventForm())
              }, 5000)
            } else {
              setIsLoading(false)
              alert('Upload Error.')
            }
          })
        }
      })
    )
  }

  const handleLoadError = () => {
    setIsLoadZazzleDesignImageSuccess(false)
    setIsDefaultFlyer(true)
    setIsLoadingEventInfo(false)
    setCanOpenTour(true)
  }

  const handleLoad = () => {
    setIsLoadZazzleDesignImageSuccess(true)
    setIsDefaultFlyer(false)
    setIsLoadingEventInfo(false)
    setCanOpenTour(true)
  }

  const onClickRefresh = (e) => {
    e.stopPropagation()
    if (designId) {
      setIsLoadingEventInfo(true)

      dispatch(
        fetchZazzleEventInfo({
          design_id: designId,
          refresh: true,
          onSuccessCallback: data => {
            if (data?.design_file_url?.Front && data?.design_file_url?.Front !== currentImageUrl) {
              setCurrentImageUrl(data.design_file_url.Front)
              setIsDefaultFlyer(false)
            } else {
              setIsLoadingEventInfo(false)
            }
          },
          onErrorCallback: () => {
            setIsLoadingEventInfo(false)
          }
        })
      )
    }
  }

  const renderDesignActions = (showIcon, editButtonText = 'Edit') => {
    return (
      <div className={styles['design-actions']}>
        <Button
          className={styles['refresh-button']}
          onClick={onClickRefresh}
          disabled={isLoadingEventInfo || !designId}
        >
          {
            showIcon && (
              <SvgIcon className={styles['refresh-icon']} icon={refreshIcon} width="14" />
            )
          }
          <span>Refresh</span>
        </Button>

        <Button
          href={`${process.env.ZAZZLE_BASE_URL}/pd/spp/dz-${designId}`}
          target="_blank"
          rel="noopener noreferrer"
          className={styles['edit-design-button']}
          wrapperElement="a"
          disabled={!designId}
          onClick={e => e.stopPropagation()}
        >
          <span>{editButtonText}</span>
          <SvgIcon className={styles['arrow-icon']} icon={ArrowRightOutlined} width="14" />
        </Button>
      </div>
    )
  }

  const renderSelectedFlyer = () => {
    const customFlyerConfig = flyerDesignLayoutConfig.find(item => item.slug === 'custom')
    const isCustomFlyer = customFlyerConfig?.designIds.includes(Number(selectedFlyerNumber))
    const customFlyerDetail = { design_number: '8', design_slug: 'hobnob:custom' }

    const selectedFlyerDetail = isCustomFlyer
      ? customFlyerDetail
      : flyerList.find(flyer => flyer.design_number === selectedFlyerNumber)

    if (selectedFlyerDetail) {
      if (selectedFlyerDetail.video_bg_slug) {
        return (
          <VideoFlyerCard
            key={selectedFlyerDetail.design_number}
            ref={videoFlyerRef}
            flyerDetail={selectedFlyerDetail}
            isThumbnail
            showCheckIcon={false}
          />
        )
      }
      return (
        <FlyerCard
          key={selectedFlyerDetail.design_number}
          ref={flyerRef}
          flyerDetail={selectedFlyerDetail}
          isThumbnail
          showCheckIcon={false}
        />
      )
    } else if (isZazzleImageUpload && blurredImageUrl) {
      return (
        <div className={cn(styles['zazzle-flyer-card'], isDefaultFlyer && styles['default-flyer-card'])}>
          {isDefaultFlyer ? (
            <img
              src={defaultFlyerImg}
              alt="Default Flyer"
              className={styles['default-flyer-img']}
            />
          ) : (
            <div className={styles['flyer-img-wrapper']}>
              <img
                src={blurredImageUrl}
                alt="Flyer with blur background"
                onLoad={handleLoad}
                onError={handleLoadError}
                className={styles['flyer-img']}
              />
            </div>
          )}

          {isLoadingEventInfo && (
            <LoadingComponent
              loading
              dark
              content="Loading..."
              containerStyle={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 2 }}
            />
          )}

          {
            isLoadZazzleDesignImageSuccess ? (
              <div className={styles['design-instructions']}>
                <div className={styles['instruction-text']}>
                  Go back to Zazzle to edit and click refresh to update
                </div>
                {renderDesignActions(
                  false,
                  'Edit'
                )}
              </div>
            ) : (
              <div className={styles['error-instructions']}>
                <div className={styles['error-overlay']}>
                  <span>It looks like we couldn't retrieve the design - please try again or go back to Zazzle to download the image to upload as a flyer</span>
                </div>
                {renderDesignActions(
                  true,
                  'Go Back to Zazzle'
                )}
              </div>
            )
          }
        </div>
      )
    } else if (!(isLoading || isImageSearchLoading) && isZazzleImageUpload && !blurredImageUrl) {
      return (
        <div className={cn(styles['zazzle-flyer-card'], styles['loading-flyer-card'])}>
          <LoadingComponent
            loading
            dark
            content="Loading..."
            containerStyle={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 2 }}
          />
        </div>
      )
    }
  }

  const getTitle = () => {
    return currentView === 'detail' ? 'Create Event' : 'Design'
  }

  const handleClickBack = () => {
    if (currentView === 'detail') {
      onNavigationList()
    } else if (currentView === 'flyer') {
      dispatch(changeEventForm({
        flyer_design_number: initialFlyerRef.current.flyer_design_number,
        flyer_design_slug: initialFlyerRef.current.flyer_design_slug,
        checkedFlyer: initialFlyerRef.current.checkedFlyer
      }))

      dispatch(setCustomFlyerURL(initialCustomFlyerURLRef.current))
      setSelectedFlyerNumber(initialFlyerRef.current.flyer_design_number)
      setTimeout(() => {
        setCurrentView('detail')
      }, 0)
    }
  }

  const renderHeaderLeft = () => {
    return (
      <img src={currentView === 'detail' ? CloseIcon : BackIcon} onClick={handleClickBack} width="12" />
    )
  }

  const renderRightButtons = () => {
    if (currentView === 'flyer') {
      return (
        <section className={styles['action-container']}>
          <div className={styles['action-item']}>
            <CustomFlyerPicker>
              <img src={ImagesIcon} width={'24px'} />
            </CustomFlyerPicker>

            <SvgIcon
              className={styles['search-icon']}
              icon={SearchIcon}
              onClick={() => setIsOpenImageSearch(true)}
              width={24}
            />
          </div>
        </section>
      )
    }
  }

  const handleImageSearchSave = async (url) => {
    setIsImageSearchLoading(true)

    try {
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache'
      })

      if (response.ok) {
        const blob = await response.blob()
        const reader = new FileReader()

        reader.onload = (e) => {
          dispatch(setCustomFlyerURL(e.target.result))
          setIsOpenImageSearch(false)
          setIsImageSearchLoading(false)
        }

        reader.onerror = () => {
          throw new Error('Failed to read blob')
        }

        reader.readAsDataURL(blob)
        return
      } else {
        throw new Error(`HTTP ${response.status}`)
      }
    } catch (fetchError) {
      console.warn('Fetch failed, trying Image method:', fetchError)

      try {
        await loadImageWithCanvas(url)
        setIsOpenImageSearch(false)
        setIsImageSearchLoading(false)
      } catch (imageError) {
        console.error('Both methods failed:', imageError)
        message.error('Failed to load image. Please try a different image.')
        setIsOpenImageSearch(false)
        setIsImageSearchLoading(false)
      }
    }
  }

  const handleRevert = () => {
    setIsRevertZazzleModalVisible(true)
  }

  const onClose = () => {
    setIsRevertZazzleModalVisible(false)
  }

  const onRevertZazzleImage = () => {
    if (selectedFlyerNumber) {
      dispatch(setCustomFlyerURL(null))
      setSelectedFlyerNumber(null)
      setIsRevertZazzleModalVisible(false)
    }
  }

  const loadImageWithCanvas = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.crossOrigin = 'anonymous'

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas')
          const ctx = canvas.getContext('2d')

          canvas.width = img.naturalWidth
          canvas.height = img.naturalHeight
          ctx.drawImage(img, 0, 0)

          const dataURL = canvas.toDataURL('image/jpeg', 0.8)
          dispatch(setCustomFlyerURL(dataURL))
          resolve(dataURL)
        } catch (error) {
          reject(error)
        }
      }

      img.onerror = () => {
        reject(new Error('Image load failed'))
      }

      img.src = url
    })
  }

  const handleCloseIntroductionModal = () => {
    setShowZazzleIntroductionPageModal(false)
  }

  return (
    <DashboardContainer
      title={getTitle()}
      left={renderHeaderLeft()}
      right={renderRightButtons()}
      width={88}
    >
      {currentView === 'detail' && (
        <div className={styles['input-all-options']}>
          <div
            className={cn(styles['flyer-container'], { [styles['flyer-container-zoom']]: isZoomFlyer })}
            onClick={() => {
              if (isMobile()) {
                setIsZoomFlyer(!isZoomFlyer)
              }
            }}
          >
            {renderSelectedFlyer()}

            <div className={styles['flyer-icons-container']}>
              <img
                onClick={handleOpenFlyerModal}
                src={PreviewIcon}
                width="54"
                className={styles['preview-icon']}
                alt="preview"
              />

              {
                selectedFlyerNumber && (
                  <div className={cn(styles['revert-icon'])} onClick={handleRevert}>
                    <SvgIcon icon={RevertIcon} width="24" />
                  </div>
                )
              }

            </div>
          </div>

          <InputOptions
            onSubmit={handleSubmit}
            customSettingNode={
              <div className={styles['settings-button']}>
                <img src={SettingIcon} width="24" />
                Settings
              </div>
            }
            showBackButton={false}
            saveButtonText="SAVE"
            warningText="Please make sure your Event Information matches your flyer"
            showTimeZoneReminder
            showCheckMarkIcon
            canOpenTour={canOpenTour}
            showHobnobSpace={false}
          />
        </div>
      )}

      {currentView === 'flyer' && (
        <div className={styles['flyer-selection-container']}>
          <div className={cn(styles['back-button'])} onClick={handleBackToDetail}>
            <img src={BackIcon} width="12" />
          </div>

          <div className={styles['create-wrapper']}>
            <header className={styles['create-header-wrapper']}>
              <h1>Design</h1>
              <div
                className={styles['image-search-label-wrapper']}
                onClick={() => setIsOpenImageSearch(true)}
              >
                <SvgIcon className={styles['search-icon']} icon={SearchIcon} />
                <span>Image Search</span>
              </div>
            </header>
            <div className={styles['create-step-wrapper']}>
              <SelectFlyerStep onButtonClick={handleChangeFlyer} onClickBack={handleBackToDetail} type="edit" />
            </div>
          </div>
        </div>
      )}

      {
        isOpenImageSearch && currentView === 'flyer' && (
          <ImageSearchModal
            eventName={eventForm.name}
            show={isOpenImageSearch}
            handleCancel={() => setIsOpenImageSearch(false)}
            handleImageSearchSave={handleImageSearchSave}
          />
        )
      }

      <ZazzleIntroductionPageModal
        show={showZazzleIntroductionPageModal}
        onClose={handleCloseIntroductionModal}
      />

      <HobnobModal
        visible={isRevertZazzleModalVisible}
        destroyOnClose
        maskClosable={false}
        fullModal
        onCancel={onClose}
      >
        <div className={styles['revert-zazzle-modal-container']}>
          <div className={styles['revert-zazzle-modal-content']}>
            Would you like to revert to the original <strong>Zazzle</strong> design?
          </div>

          <div className={styles['revert-zazzle-modal-buttons']}>
            <Button
              type="outline"
              onClick={onClose}
              className={styles['revert-zazzle-modal-cancel-button']}
            >
              No
            </Button>

            <Button
              type="primary"
              onClick={onRevertZazzleImage}
              className={styles['revert-zazzle-modal-confirm-button']}
            >
              Yes
            </Button>
          </div>
        </div>
      </HobnobModal>

      <LoadingComponent loading={isLoading || isImageSearchLoading} dark />
    </DashboardContainer>
  )
}

ZazzleCreateEvent.propTypes = {
  designId: PropTypes.string,
  imageUrl: PropTypes.string,
  source: PropTypes.string
}

export default ZazzleCreateEvent
